import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { motion } from 'framer-motion'; // Import Framer Motion   
import { auth, app } from '../../firebase'; // Import Firebase authentication
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'; // Import sign in method  
//@ts-ignore  
import fullLogo from '../../assets/logo/ByteSplash_Logo.png';
import { getFirestore } from 'firebase/firestore';
import { useAppDispatch } from '../../store/hooks';
import { loginUser } from '../../store/slices/userSlice';
import { useLoading } from '../../hooks/useLoading';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const db = getFirestore(app);
  const auth = getAuth(app);
  const dispatch = useAppDispatch();
  const { setIsLoading} = useLoading();
  const [isLoading, setIsSLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation  
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill all fields",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setIsSLoading(true);

    try {
      // Sign in with Firebase  
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get the user's access token  
      const user = userCredential.user;
      const accessToken = await user.getIdToken(); // Retrieve the access token  

      // You can save user roles in your Firestore or Realtime Database and fetch them if needed  
      const userInfo = {
        emailId: user.email,
        role: "USER_ROLE", // Placeholder for actual roles, you should replace this based on your database  
        accessToken,
        uid: user.uid
      };

    //  const resultAction = await dispatch(loginUser({ email, password })).unwrap();
      // Store user info in local storage  
      localStorage.setItem('user', JSON.stringify(userInfo));

      // toast({
      //   title: "Welcome back!",
      //   description: "You have successfully logged in",
      // });
      window.location.href="/dashboard";
      setIsLoading(false);
    setIsSLoading(false);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: JSON.parse(JSON.stringify(error.code)),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsSLoading(false);
    }
  };


  useEffect(() => {
    const checkUserLoggedIn = async () => {
      // Check local storage for user data  
      const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;

      if (userData && userData.accessToken) {
        try {
          // Verify the access token with Firebase Auth  
          // await signInWithCustomToken(auth, userData.accessToken);  
          // If successful, do nothing (stay on the dashboard)  
          setIsLoading(false);
          setIsSLoading(false);
          window.location.href= "/dashboard";
        } catch (error) {
          setIsLoading(false);
        setIsSLoading(false);
          console.error("Error during authentication:", error);
          // Redirect to login on error or invalid token  
          navigate("/login");
        }
      } else {
        setIsLoading(false);
        setIsSLoading(false);
        // Redirect to login if no user data found  
        //navigate("/login");
      }
    };

    checkUserLoggedIn();
  }, [navigate, auth]);

  if (isLoading) return <p>Loading...</p>;

  return (
    <div className="flex flex-col items-center justify-center max-h-screen">
      <img src={fullLogo} height={40} width={280} alt="Full Logo" />
      <h1 className="text-xl text-gray-400 font-semibold mb-4 text-center">Learning Management System</h1>
      <motion.div
        className="w-full max-w-md p-4"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="w-full shadow-lg rounded-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-bytesplash-primary text-center">Sign In</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2 text-left">
                <Label htmlFor="email" className="text-left">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-bytesplash-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-input pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-bytesplash-primary hover:bg-bytesplash-dark"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn size={18} /> Sign in
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col" />
        </Card>
      </motion.div>
    </div>
  );
};  